import { format, isSameDay, parseISO } from 'date-fns';
import { useMemo } from 'react';

import { useTaskFiltersStore } from '@/entities/task/model/stores/task-filters';
import { useTasks } from '@/features/tasks/model/useTaskActions';
import { useAuthStore } from '@/store/auth';

import { MeetingCardHeader } from '@/components/smart/meetingCardHeader';

const MEETINGS_GROUP_ID = 6;
const SKELETON_COUNT = 4;
const DATE_FORMAT = 'yyyy-MM-dd';
const stateClassName = 'min-h-42';

type MeetingCandidate = {
  assigneeId?: number | null;
  accomplices?: unknown;
  auditors?: unknown;
  createdBy?: number | null;
  dueDate?: string | null;
  groupId?: number | null;
  isMeeting?: boolean;
};

function isParticipant(task: MeetingCandidate, userId?: number | null) {
  if (!userId) {
    return false;
  }

  const accomplices = Array.isArray(task.accomplices) ? task.accomplices : [];
  const auditors = Array.isArray(task.auditors) ? task.auditors : [];

  return (
    task.assigneeId === userId ||
    accomplices.includes(userId) ||
    task.createdBy === userId ||
    auditors.includes(userId)
  );
}

function isMeeting(task: MeetingCandidate) {
  return task.isMeeting || task.groupId === MEETINGS_GROUP_ID;
}

/** Сортируем по крайнему сроку (dueDate), план старта — только как запасной вариант. */
function meetingStartMs(task: {
  startDatePlan?: string | null;
  dueDate?: string | null;
}) {
  const iso = task.dueDate ?? task.startDatePlan;
  const ms = iso ? parseISO(iso).getTime() : NaN;
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

export const MeetingList = () => {
  const { userId } = useAuthStore();
  const { selectedDate } = useTaskFiltersStore();
  const date = format(selectedDate, DATE_FORMAT);

  const { data, isFetching } = useTasks(
    {
      page: 1,
      perPage: 200,
      dateFrom: date,
      dateTo: date,
      currentUserId: userId,
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: Boolean(userId),
      staleTime: 2 * 60 * 1000,
    }
  );

  const meetings = useMemo(() => {
    return (data?.items ?? [])
      .filter((task) => {
        if (!task.dueDate) {
          return false;
        }
        // Завершённые встречи убираем из списка активных (иначе после
        // завершения из плашки они возвращались бы при следующем рефетче).
        if (task.status === 'done' || task.backendStatus === 5) {
          return false;
        }

        return (
          isSameDay(parseISO(task.dueDate), selectedDate) &&
          isParticipant(task, userId) &&
          isMeeting(task)
        );
      })
      .sort((a, b) => meetingStartMs(a) - meetingStartMs(b));
  }, [data?.items, selectedDate, userId]);

  if (isFetching && meetings.length === 0) {
    return (
      <div className={`grid grid-cols-2 gap-10 ${stateClassName}`}>
        {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
          <div
            key={index}
            className="min-h-[7.5rem] animate-pulse rounded-[8.49px] bg-card shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.03)] ring-1 ring-black/[0.04]"
          />
        ))}
      </div>
    );
  }

  if (meetings.length === 0) {
    return (
      <div className={`grid place-items-center ${stateClassName}`}>
        <p className="text-center text-muted-foreground">
          Нет встреч на выбранную дату
        </p>
      </div>
    );
  }

  return (
    <ul className="grid grid-cols-2 gap-10">
      {meetings.map((task) => (
        <li key={task.id} className="min-h-0">
          <MeetingCardHeader task={task} variant="light" className="h-full" />
        </li>
      ))}
    </ul>
  );
};
